import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import './FdModal.css';

/*
 * FdModal – Reusable FinDesk popup modal (modernized Bootstrap-inspired).
 * Uses createPortal into a dedicated #fd-portal div.
 * CRITICAL: Overlay + modal box positioning are INLINE styles (the visibility fix).
 * Visual polish (colors, animations, typography) comes from CSS classes.
 * Dark mode: detected via document.documentElement.classList ('theme-dark').
 */

/* ── Inline style objects (POSITIONING ONLY – DO NOT REMOVE) ── */
const OVERLAY_STYLE = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100vw',
  height: '100vh',
  zIndex: 99999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  boxSizing: 'border-box',
  pointerEvents: 'auto',
};

const MODAL_STYLE = {
  width: '540px',
  maxWidth: '100%',
  maxHeight: '88vh',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
};

const MODAL_WIDE_STYLE = {
  ...MODAL_STYLE,
  width: '720px',
};

function getPortalTarget() {
  let el = document.getElementById('fd-portal');
  if (!el) {
    el = document.createElement('div');
    el.id = 'fd-portal';
    el.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;z-index:99999;pointer-events:none;';
    document.body.appendChild(el);
  }
  return el;
}

export default function FdModal({ isOpen, onClose, title, subtitle, eyebrow, wide, children, footer }) {
  const [portalTarget, setPortalTarget] = useState(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    setPortalTarget(getPortalTarget());
  }, []);

  // Modals close ONLY via the X button or a designated footer action button.
  // Backdrop clicks and the Escape key are deliberately ignored so a stray
  // click/keypress cannot discard a half-filled form (QA requirement).
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen || !portalTarget) return null;

  /* Detect dark mode from <html> or <body> class */
  const isDark = document.documentElement.classList.contains('theme-dark') ||
                 document.body.classList.contains('theme-dark');

  const themeClass = isDark ? 'fd-modal-dark' : '';
  const animClass = 'fd-modal-visible';

  return createPortal(
    <div
      ref={overlayRef}
      className={`fd-modal-overlay ${themeClass} ${animClass}`}
      style={OVERLAY_STYLE}
    >
      <div
        className={`fd-modal ${wide ? 'fd-modal-wide' : ''} ${themeClass} ${animClass}`}
        style={wide ? MODAL_WIDE_STYLE : MODAL_STYLE}
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Modal'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="fd-modal-topbar" />
        <div className="fd-modal-header">
          <div className="fd-modal-header-text">
            {eyebrow && <span className="fd-modal-eyebrow">{eyebrow}</span>}
            <h3 className="fd-modal-title">{title}</h3>
            {subtitle && <p className="fd-modal-subtitle">{subtitle}</p>}
          </div>
          <button type="button" className="fd-modal-close-btn" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="fd-modal-body">
          <div className="fd-modal-body-inner">
            {children}
          </div>
        </div>
        {footer && (
          <div className="fd-modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>,
    portalTarget
  );
}
